/** Offset do header fixo ao rolar até uma seção do perfil (mobile/desktop). */
const PROFILE_SECTION_SCROLL_OFFSET = 88;

/** Reserva inferior para a MobileBarNavigation (LayoutColapse + scroll do perfil). */
export const PROFILE_MOBILE_BOTTOM_NAV_CLEARANCE =
  "calc(104px + env(safe-area-inset-bottom, 0px))";

export interface ScrollToProfileSectionOptions {
  topOffset?: number;
  /** Garante que a base do bloco fique acima da nav flutuante. */
  bottomOffset?: number;
}

function getScrollParent(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement;

  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === "auto" || overflowY === "scroll") {
      return parent;
    }
    parent = parent.parentElement;
  }

  return null;
}

/**
 * Rola até um elemento dentro do container scrollável do layout (LayoutColapse/main).
 * Retorna true quando encontrou o elemento e iniciou o scroll.
 */
export function scrollToProfileSection(
  sectionId: string,
  options: ScrollToProfileSectionOptions = {}
): boolean {
  const topOffset = options.topOffset ?? PROFILE_SECTION_SCROLL_OFFSET;
  const bottomOffset = options.bottomOffset ?? 0;
  const element = document.getElementById(sectionId);
  if (!element) {
    return false;
  }

  const scrollParent = getScrollParent(element);

  if (scrollParent) {
    const elementRect = element.getBoundingClientRect();
    const parentRect = scrollParent.getBoundingClientRect();
    const maxScrollTop = scrollParent.scrollHeight - scrollParent.clientHeight;

    let targetTop =
      elementRect.top - parentRect.top + scrollParent.scrollTop - topOffset;

    if (bottomOffset > 0) {
      const elementBottom =
        elementRect.bottom - parentRect.top + scrollParent.scrollTop;
      const minTopToRevealBottom =
        elementBottom - scrollParent.clientHeight + bottomOffset;
      targetTop = Math.max(targetTop, minTopToRevealBottom);
    }

    scrollParent.scrollTo({
      top: Math.min(maxScrollTop, Math.max(0, targetTop)),
      behavior: "smooth",
    });
    return true;
  }

  element.scrollIntoView({ behavior: "smooth", block: "start" });
  return true;
}

/**
 * Tenta rolar até a seção com retries — útil após loading ou troca de rota.
 */
export function scrollToProfileSectionWithRetry(
  sectionId: string,
  onScrolled?: () => void,
  maxAttempts = 15,
  delayMs = 120,
  scrollOptions?: ScrollToProfileSectionOptions
): () => void {
  let attempts = 0;
  let timeoutId: number | undefined;

  const tryScroll = () => {
    attempts += 1;
    const didScroll = scrollToProfileSection(sectionId, scrollOptions);

    if (didScroll) {
      onScrolled?.();
      return;
    }

    if (attempts < maxAttempts) {
      timeoutId = window.setTimeout(tryScroll, delayMs);
    }
  };

  window.requestAnimationFrame(() => {
    timeoutId = window.setTimeout(tryScroll, 80);
  });

  return () => {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
  };
}

/** Lê o id da seção alvo a partir da URL (query `focus` ou hash legado). */
export function getProfileFocusSection(search: string, hash: string): string | null {
  const focusFromQuery = new URLSearchParams(search).get("focus");
  if (focusFromQuery) {
    return focusFromQuery;
  }

  if (hash) {
    return hash.replace("#", "");
  }

  return null;
}
